import React from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { useNavigate } from 'react-router-dom';

function formatPHP(value) {
  if (value >= 1000000) return `₱${(value / 1000000)?.toFixed(2)}M`;
  if (value >= 1000) return `₱${(value / 1000)?.toFixed(0)}K`;
  return `₱${value?.toLocaleString()}`;
}

export default function RegionDetailPanel({ region, onClose }) {
  const navigate = useNavigate();

  if (!region) return null;

  const isPositive = region?.growth >= 0;

  return (
    <div className="border border-border rounded-xl shadow-lg overflow-hidden flex flex-col h-full" style={{ background: 'var(--color-card)' }}>
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border" style={{ background: 'linear-gradient(135deg, #7F1D1D, #DC2626)' }}>
        <div>
          <h2 className="font-heading font-bold text-white text-base">{region?.name}</h2>
          <p className="font-caption text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>{region?.island} Group • {region?.provinces} Provinces</p>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-white/20 text-white transition-all duration-200 press-effect flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.15)' }}
          aria-label="Close panel"
        >
          <Icon name="X" size={16} color="currentColor" strokeWidth={2} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
        {/* Growth Badge */}
        <div className="flex items-center gap-2 mb-4">
          <span
            className={`flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full`}
            style={isPositive ? { background: 'rgba(34, 197, 94, 0.12)', color: '#22C55E', border: '1px solid rgba(34, 197, 94, 0.25)' } : { background: 'rgba(239, 68, 68, 0.12)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.25)' }}
          >
            <Icon name={isPositive ? 'TrendingUp' : 'TrendingDown'} size={14} color="currentColor" strokeWidth={2.5} />
            {isPositive ? '+' : ''}{region?.growth}% YoY Growth
          </span>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Total Revenue', value: formatPHP(region?.revenue), icon: 'DollarSign', color: '#DC2626' },
            { label: 'Total Orders', value: region?.orders?.toLocaleString(), icon: 'ShoppingCart', color: '#D4AF37' },
            { label: 'Active Accounts', value: region?.accounts, icon: 'Users', color: '#22C55E' },
            { label: 'Coverage Rate', value: `${region?.coverage}%`, icon: 'MapPin', color: '#8B92A5' },
          ]?.map((kpi) => (
            <div key={kpi?.label} className="rounded-lg p-3" style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon name={kpi?.icon} size={13} color={kpi?.color} strokeWidth={2} />
                <p className="font-caption text-xs text-muted-foreground">{kpi?.label}</p>
              </div>
              <p className="font-data text-base font-bold" style={{ color: kpi?.color }}>{kpi?.value}</p>
            </div>
          ))}
        </div>

        {/* Top Products */}
        <div className="mb-4">
          <h3 className="font-heading font-semibold text-slate-800 text-sm mb-2">Top Products</h3>
          <div className="flex flex-col gap-2">
            {region?.topProducts?.map((product, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="font-data text-xs text-slate-400 w-4 flex-shrink-0">#{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-caption text-xs text-slate-700 truncate">{product?.name}</span>
                    <span className="font-data text-xs text-slate-500 ml-2 flex-shrink-0">{product?.share}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${product?.share}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sales Team */}
        <div className="mb-4">
          <h3 className="font-heading font-semibold text-slate-800 text-sm mb-2">Sales Team</h3>
          <div className="flex flex-col gap-2">
            {region?.salesTeam?.map((member, idx) => (
              <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading text-xs font-bold text-blue-800">
                      {member?.name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <p className="font-caption text-xs font-medium text-slate-800">{member?.name}</p>
                    <p className="font-caption text-xs text-slate-500">{member?.role}</p>
                  </div>
                </div>
                <span className="font-data text-xs font-semibold text-emerald-700">{formatPHP(member?.sales)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Coverage Gap Alert */}
        {region?.coverage < 80 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={14} color="var(--color-warning)" strokeWidth={2} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-caption text-xs font-medium text-amber-800">Coverage Gap Detected</p>
                <p className="font-caption text-xs text-amber-700 mt-0.5">
                  {100 - region?.coverage}% of accounts in this region are not being regularly visited.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="p-4 border-t border-slate-100 flex flex-col gap-2">
        <Button
          variant="default"
          size="sm"
          fullWidth
          iconName="Users"
          iconPosition="left"
          iconSize={14}
          onClick={() => navigate('/account-management')}
        >
          View Accounts
        </Button>
        <Button
          variant="outline"
          size="sm"
          fullWidth
          iconName="BarChart2"
          iconPosition="left"
          iconSize={14}
          onClick={() => navigate('/performance-analytics')}
        >
          View Analytics
        </Button>
      </div>
    </div>
  );
}